import React, { useCallback, useEffect, useState } from 'react';
import { Tabs, Tab, Alert } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Message } from '../Message';
import { PageTitle } from '../PageTitle';
import MonitoringAlerts from './monitoring_alerts';
import AlertList from './monitor_alerts_list';
import MonitoringMapView from './monitoring_map_view';
import MonitorChecklistsList from './monitor_checklists_list';
import MonitoringInfrastructureView from './monitoring_infrastructure_view';
import { EntityService, GroupingsService } from '../../services';
import { AccessHelper } from '../../helpers';
import i18next from "i18next";
import { extractAreaFilterOptionsFromEntities, extractStatusOptionsFromEntities, extractDataTypeOptionsFromEntities } from "../Entity";
import { Redirect } from 'react-router-dom';

const TAB_CONFIGS = {
  'assets' : { index: 1, needsEntityData: true },
  'events': { index: 2, needsEntityData: true },
  'checklists': { index: 3, needsEntityData: false },
  'maps': { index: 4, needsEntityData: true }, 
  'infrastructure': { index: 5, needsEntityData: true }
};

const INDEX_TO_TAB_NAME = [
  'assets',
  'events',
  'checklists',
  'maps',
  'infrastructure'
];

const MonitoringLocationDetail = ({ me, match, history, location }) => {
  const [currentGrouping, setCurrentGrouping] = useState(location.state?.grouping);
  const [loading, setLoading] = useState(null);
  const [redirectTo, setRedirectTo] = useState(null);
  const [entityDataCache, setEntityDataCache] = useState(location.state?.entityDataCache);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(localStorage.getItem("global_success_message") ?? location.state?.successMessage);

  const currentGroupingId = match.params?.deviceGroupingId;

  useEffect(() => {
    if (!TAB_CONFIGS[match.params.tab]) {
      setRedirectTo(`${INDEX_TO_TAB_NAME[0]}`);
    } else {
      if (TAB_CONFIGS[match.params.tab]?.needsEntityData && !entityDataCache) {
        refreshEntityDataCache();
      }

      if (!currentGrouping) {
        GroupingsService.get(match.params.deviceGroupingId).then((response) => {
            setCurrentGrouping(response.grouping);
          }).catch((error) => {
            setErrorMessage(i18next.t('messages.grouping-error'));
            console.error(error);
          });
      }
    }
  }, [me, match, location]);

  const messageDismissCallback = () => {
    localStorage.removeItem('global_success_message');
    setSuccessMessage(null);
    setErrorMessage(null);
  }

  const refreshEntityDataCache = useCallback((whenDone) => {
    setLoading(true);

    EntityService.findAllForGrouping(currentGroupingId).then((response) => {
      const entities = response.entities
        .filter((entity) => {
          if (entity.entityTypeToken === 'asset') {
            return entity.currentMeasurements.filter((measure) => measure.endTime == null).length > 0;
          } else {
            return true;
          }
        });

      const entityData = {
          entities: entities,
          areaFilterOptions: extractAreaFilterOptionsFromEntities(entities, location.state?.areaFilters),
          statusOptions: extractStatusOptionsFromEntities(entities, location.state?.statusOptions),
          datatypeOptions: extractDataTypeOptionsFromEntities(entities),
          filtersSet: location.state && (location.state.statusOptions || location.state.areaFilters)
      };
      setEntityDataCache(entityData);
      setLoading(false);

      if (whenDone) {
        whenDone(entityData);
      }
    }).catch(error => {
      console.error(error);
      setErrorMessage(error.message);
      setLoading(false);
    });
  }, [currentGroupingId]);

  const handleTabSelect = (tabIndex) => {
    history.push({
      pathname: `/monitoring/locations/${currentGroupingId}/${INDEX_TO_TAB_NAME[tabIndex-1]}`,
      state: {
        grouping: currentGrouping,
        entityDataCache: entityDataCache
      }
    });
  };

  if (redirectTo) {
    return <Redirect push to={{ pathname: `/monitoring/locations/${currentGroupingId}/assets`, state: location.state }} />;
  } else if (currentGrouping) {
    return (
      <div className="animated fadeIn">
        <div className="row">
          <PageTitle 
            title={currentGrouping.name}
            backLinkWithParams={{ link: { pathname: location.state?.fromPath ?? '/monitoring/locations', search: location.state?.backLinkSearch, state: location.state } }}
            refreshFunction={refreshEntityDataCache} 
          />
        </div>

        { currentGrouping.actionsEnabled ? null : <Alert bsStyle='warning'>{ i18next.t('messages.alert-actions-disabled') }</Alert> }

        { (errorMessage || successMessage) && <Message messages={{ state: { successMessage: successMessage } }} errorMessage={errorMessage} messageDismissCallback={messageDismissCallback} /> }

        <div className="op-content-box monitoring-location-map">
          <Tabs defaultActiveKey={TAB_CONFIGS[match.params.tab].index} id="locations-top-tabs" onSelect={handleTabSelect} mountOnEnter={true}>
            <Tab eventKey={1} title={ i18next.t('menus.assets') }>
              { loading && (<div className="loader">{ i18next.t('loading') }</div>) }
              { !loading && entityDataCache &&
              <MonitoringAlerts
                  grouping={currentGrouping}
                  entities={entityDataCache.entities}
                  statusOptions={entityDataCache.statusOptions}
                  areaFilterOptions={entityDataCache.areaFilterOptions}
                  datatypeOptions={entityDataCache.datatypeOptions}
              />
              }
            </Tab>

            <Tab eventKey={2} title={ i18next.t('menus.events') }>
              { loading && (<div className="loader">{ i18next.t('loading') }</div>) }
              { !loading &&
              <AlertList
                  deviceGroupingId={currentGroupingId}
                  grouping={currentGrouping}
                  location={location}
                  history={history}
                  match={match}
                  onSuccess={setSuccessMessage}
                  onError={setErrorMessage}
              />
              }
            </Tab>

            { AccessHelper.userHasAccess(me.availableRoutes, '/monitoring/locations/:deviceGroupingId/checklists') &&
            <Tab eventKey={3} title={ i18next.t('menus.checklists') }>
              { loading && (<div className="loader">{ i18next.t('loading') }</div>) }
              { !loading &&<MonitorChecklistsList grouping={currentGrouping} history={history} /> }
            </Tab>
            }

            <Tab eventKey={4} title={ i18next.t('menus.maps') }>              
              { loading && (<div className="loader">{ i18next.t('loading') }</div>) }
              { !loading && entityDataCache &&
              <MonitoringMapView
                  grouping={currentGrouping}
                  entities={entityDataCache.entities}
                  statusOptions={entityDataCache.statusOptions}
                  areaFilterOptions={entityDataCache.areaFilterOptions}
              />
              }
            </Tab>

            { AccessHelper.userHasAccess(me.availableRoutes, '/monitoring/locations/:deviceGroupingId/infrastructure') &&
            <Tab eventKey={5} title={ i18next.t('menus.infrastructure') }>
              { loading && (<div className="loader">{ i18next.t('loading') }</div>) }
              { !loading && entityDataCache &&
              <MonitoringInfrastructureView
                grouping={currentGrouping}
                entities={entityDataCache.entities}
                statusOptions={entityDataCache.statusOptions}
                areaFilterOptions={entityDataCache.areaFilterOptions}
              />
              }
            </Tab>
            }
          </Tabs>
        </div>
      </div>
    );
  } else {
    return (<div className="loader">{ i18next.t('loading') }</div>);
  }
}

export default connect(({user}) => ({me: user.me}))(MonitoringLocationDetail);
